'use client';

// One conversation: the turns, the queue, and the composer.
//
// It takes the thread it is drawing rather than reading "the one in front" off
// the provider, and that is the whole of what lets several be open at once. It
// used to read a dozen values that could only mean the front conversation —
// `turns`, `draft`, `busy`, `queue` — which was the same thing as this for
// exactly one window and nothing at all for three: every window would have
// shown the same turns, and typing in one would have typed in all of them.
//
// What follows from that, and is the part worth not breaking: everything here
// is either on the thread or local to this component. The scroller, the
// composer's field, the model popover and the dock's measured height are all
// per-window now. Anything that reaches the provider names its thread.

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Layers01Icon, Sent02Icon, StopIcon } from '@hugeicons/core-free-icons';
import { MODELS, MODEL_LABELS } from '@/lib/models';
import { whereLabel } from '@/lib/sessions';
import { useConsole, useMeasuredHeight, type Thread } from '../console';
import { ProjectPicker } from '../parts';
import { TurnView } from './parts';

/**
 * The model popover's own state, machinery and all.
 *
 * This lived in the provider while there was one composer in the app. There are
 * several now, so a single open flag would open the wrong menu and a single
 * pair of refs would decide "did the click land inside this control" by asking
 * about somebody else's control. It is a fact about a composer, so it lives
 * with the composer.
 */
function useModelMenu() {
  const [open, setOpen] = useState(false);
  // Two, because the popover is not inside the control that opens it — the menu
  // had to move out of the composer field's backdrop root to get its blur back,
  // so "did the click land on this control" is now two questions.
  const pick = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  // Closes on Escape and on a click anywhere outside it. There is no scrim to
  // catch that click the way the drawer's does — a scrim dims the page, which is
  // right for something that takes over the screen and wrong for a three-item
  // list picking a setting — so this listens for the click directly instead.
  // `pointerdown` rather than `click`, so the popover is gone before whatever
  // was underneath it reacts to the same press.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      // Both, and the menu first: it is the one that is no longer a descendant
      // of the trigger, so checking only the trigger would close the popover on
      // the press that was choosing from it.
      if (menu.current?.contains(t) || pick.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  /*
   * Where the focus ring goes while the popover is open, and where it comes back
   * to when it shuts.
   *
   * A listbox that does not take focus is a listbox in name only: it announces
   * three options to a screen reader and then leaves the ring on the button that
   * opened it, so Escape has nothing to restore and the arrow keys below have
   * nothing to move. Opening lands on the model already chosen — that is where
   * someone is deciding *from*, and it means one press either way is usually the
   * whole interaction.
   *
   * Focus moves for a mouse too. It costs a mouse user nothing (`:focus-visible`
   * draws no ring for a pointer) and it keeps one rule rather than two.
   */
  useEffect(() => {
    if (open) {
      // After the class that makes it visible has landed — `visibility: hidden`
      // is not focusable, and React has only just committed it.
      const id = requestAnimationFrame(() => {
        const el = menu.current;
        const chosen = el?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
        (chosen ?? el?.querySelector<HTMLElement>('[role="option"]'))?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
    // Shut. Only take the focus back if it is still in there — a click elsewhere
    // on the page closed this too, and hauling the ring back to the trigger
    // afterwards would steal it from wherever it had just gone.
    if (menu.current?.contains(document.activeElement)) {
      pick.current?.querySelector('button')?.focus();
    }
  }, [open]);

  /*
   * Up and down the list, wrapping, plus Home and End.
   *
   * Roving focus rather than `aria-activedescendant`: the options are real
   * buttons and were already reachable by Tab, so moving the focus itself keeps
   * one notion of "the current one" instead of two that can disagree. Enter and
   * Space need nothing here — a button already answers both.
   */
  const onMenuKey = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const items = [...(menu.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])];
    if (!items.length) return;
    // Arrowing before anything inside has focus starts at the top rather than
    // wrapping to the bottom, which is what `-1 + 1` would otherwise give.
    const at = items.indexOf(document.activeElement as HTMLElement);
    const to =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? items.length - 1
          : at < 0
            ? 0
            : (at + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    // The page scrolls on these keys otherwise, and the popover sits at the
    // bottom of a conversation that has somewhere to scroll to.
    e.preventDefault();
    items[to].focus();
  }, []);

  /** Down (or up) from the trigger opens the list, the way a select does. */
  const onTriggerKey = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    setOpen(true);
  }, []);

  return { open, setOpen, pick, menu, onMenuKey, onTriggerKey };
}

export function Conversation({ thread }: { thread: Thread }) {
  const {
    workspaces, error, toggleProject, addWorkspace, queueingIn,
    setDraft, setPulse, chooseModel, submitDraft, unqueue, release, onKeyDown, stopTurn,
    lendInput, sessions, lowerThread,
  } = useConsole();

  const key = thread.key;
  const { session, turns, draft, queue, busy, model, modelError, pulse } = thread;
  const queueing = queueingIn(thread);
  // Destructured rather than kept as an object, so the two refs reach the JSX
  // as plain names: `menu.pick` reads to the lint rule as a ref being touched
  // during render, which it is not, and there is no way to say so from a
  // property access.
  const {
    open: menuOpen,
    setOpen: setMenuOpen,
    pick: modelPick,
    menu: modelMenu,
    onMenuKey,
    onTriggerKey,
  } = useModelMenu();

  // This window's own, not the app's. Two conversations expanded side by side
  // scroll independently and have composers of different heights.
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  // Everything that floats over the conversation, measured as one piece: the
  // queue grows and shrinks, the field grows as you type, and what the stream
  // has to keep clear at the bottom is whatever the two come to. Scoped, so the
  // answer lands on this window rather than on the document — see
  // `useMeasuredHeight`.
  const dock = useMeasuredHeight('--dock-height', true);

  // Lend the field to the provider for as long as this window is on screen, so
  // that "put the cursor back" can find it — see `lendInput`. By key, because
  // there may be two other windows doing the same thing.
  useEffect(() => {
    lendInput(key, input.current);
    return () => lendInput(key, null);
  }, [key, lendInput]);

  // Follow the reply down. This window's scroller and this thread's turns: a
  // reply arriving in the conversation beside this one must not move it.
  useEffect(() => {
    // Nothing said yet, so there is nothing to follow — and a new chat is a list
    // of projects with the composer under it, which this would scroll straight
    // past. A conversation opens at its live edge; a chat that has not started
    // opens at its top, where the heading is.
    if (!turns.length) return;
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  // What the rail calls this thread, so the window and its tab agree.
  const headline = session
    ? (sessions.find(s => s.session_id === session.session_id)?.title ?? 'Conversation')
    : 'New chat';

  return (
    <>
        {/* No `onScroll` here: the phone header sits over the middle column, not
            over this window, so what the conversation is scrolled to is none of
            its business. The ref stays — following a turn to the bottom is this
            window's own job. */}
        <div className="stream" ref={scroller}>
          {/* The cards and the composer, in tandem — this is the new-chat face
              rather than a screen in front of it. They come off as soon as the
              conversation exists, because `send` fixes the working directory
              when it creates the session: a card still toggling after that would
              be offering a choice that no longer decides anything.
              *
              Nothing here has to be pressed. The heading says so rather than
              leaving someone looking for the one they are supposed to pick. */}
          {!session && (
            <div className="ws-choose">
              <div className="empty">
                <h1>New chat</h1>
                <p>
                  Say what you need. Adding a project or two gives it somewhere to start —
                  they are context, not a fence, and none is a fine answer.
                </p>
              </div>
              <ProjectPicker
                workspaces={workspaces}
                chosen={thread.chosen.map(w => w.name)}
                onToggle={ws => toggleProject(key, ws)}
                // Ticked as soon as it lands. Somebody who just added a repo
                // wants it in this conversation; making them find the new card
                // and press it would be asking twice for one decision.
                onAdd={async spec => {
                  const added = await addWorkspace(spec);
                  toggleProject(key, added);
                  return added;
                }}
                loading={!workspaces.length && !error}
              />
            </div>
          )}

          {/* Which conversation this is. The window's own head carries it too,
              but that scrolls away with the first screen — a page's name, not a
              bar that follows you down it. */}
          {turns.length > 0 && (
            <header className="thread-head">
              <h1 className="thread-title">{headline}</h1>
              <span className="thread-where">{whereLabel(thread.chosen.map(w => w.name))}</span>
            </header>
          )}

          {turns.map((turn, i) => (
            <Fragment key={i}>
              <TurnView turn={turn} streaming={busy && i === turns.length - 1} />
              {thread.restoredCount > 0 && i === thread.restoredCount - 1 && (
                <div className="resume-rule">
                  <span>resumed</span>
                </div>
              )}
            </Fragment>
          ))}
        </div>

        {/* Everything that floats over the conversation, measured as one piece:
            the queue grows and shrinks, and the field grows as you type, and
            what the stream has to keep clear at the bottom is whatever the two
            of them currently come to. */}
        <div className="dock" ref={dock}>
        {/* What is waiting to be sent, and the only place it can be taken back.
            It sits against the composer rather than in the stream because it is
            not conversation yet — nothing here has been said. */}
        {!!queue.items.length && (
          <div className="queued">
            <div className="queued-head">
              <span className="queued-label">
                {queue.held ? 'Held' : 'Queued'} · {queue.items.length}
              </span>
              {/* Only when something has gone wrong. A running queue explains
                  itself — the rows are there, the turn is visibly going, and
                  they leave when it ends — so narrating that was the panel
                  reading its own behaviour aloud.
                  *
                  Held is the case that does need words, because it looks
                  identical and behaves the opposite way, and because it covers
                  more than one thing: a turn stopped, a turn that failed, a
                  connection lost, a page that came back to find the turn already
                  over. The note says the part true of all of them and lets the
                  button say the rest. */}
              {queue.held && <span className="queued-note">Nothing here has been sent.</span>}
              {queue.held && (
                <button className="queued-release" onClick={() => release(key)}>
                  Send now
                </button>
              )}
            </div>

            <ol className="queued-list">
              {queue.items.map((item, i) => (
                <li className="queued-row" key={item.id}>
                  <span className="queued-text" title={item.text}>
                    {item.text}
                  </span>
                  <button
                    className="queued-drop"
                    onClick={() => unqueue(key, item.id)}
                    aria-label={`Delete queued message ${i + 1}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* A model switch that did not take, said where the switch was made.
            `role="alert"` because the thing it is explaining — the picker going
            back to the model you just changed away from — is a visual event a
            screen reader gets no notice of at all. */}
        {modelError && (
          <div className="composer-note" role="alert">
            {modelError}
          </div>
        )}

        {/* One floating field holding its own controls, rather than a tray with
            a rule above it. Nothing separates it from the conversation now, so
            what marks it as the place you type is that it is lit and lifted off
            the page rather than fenced off from it. */}
        <div className="composer">
          {/* Carries the field's width and position so the model menu has
              something to hang off that is NOT inside the field. The field sets
              `backdrop-filter`, and an element with one is a backdrop root for
              everything under it — a descendant's own blur then samples only
              what its ancestor painted, which here was nothing, so the menu's
              blur silently did nothing at all and the conversation read through
              it sharp. Verified by stripping the menu's fill and looking. */}
          <div className="composer-anchor">
          <div className="composer-field traced">
            {/* Remounted per send, which is the whole trick: an element that has
                just appeared runs its animation from the beginning, where
                re-adding a class to a live one does nothing. Removed again when
                it finishes, so nothing is left animating. */}
            {pulse > 0 && (
              <span
                key={pulse}
                className="pulse"
                aria-hidden="true"
                onAnimationEnd={() => setPulse(key, 0)}
              />
            )}

            <textarea
              ref={input}
              value={draft}
              onChange={e => setDraft(key, e.target.value)}
              onKeyDown={e => onKeyDown(key, e)}
              // What the field is for, and nothing about how to work it. The
              // keyboard hint that used to be here described a keyboard half the
              // people reading it do not have, and on a phone it wrapped to three
              // lines to say so. Enter still sends and Shift+Enter still breaks a
              // line for anyone typing on keys.
              placeholder={queueing ? 'Queue a message…' : 'Message…'}
              rows={1}
            />

            {/* Icons, and so labelled rather than captioned: at this size the
                word was most of the button. The placeholder above already says
                which of send and queue is about to happen, so the icon is
                confirming a state rather than being the only thing that names
                it — and every one of these carries its name for anything not
                reading the picture. */}
            <div className="composer-actions">
              {/* A hand-built popover rather than the OS picker a native <select>
                  would give a phone for free. It costs the things a <select>
                  earns without trying — focus handling, dismissal, scroll — which
                  is why the model picker used to be one; but nothing on the page
                  reads as this app's own chrome then, one control breaks the set,
                  and a woodland interface with a flat system menu dropped into it
                  is exactly the seam the design is meant not to have.

                  It is the leftmost control because it is a setting, not an
                  action — send and stop stay together at the end where the thumb
                  goes, and nothing has moved for anyone who never opens this. */}
              <div className="model-pick" ref={modelPick}>
                <button
                  type="button"
                  className={`model-pick-trigger${menuOpen ? ' is-open' : ''}`}
                  onClick={() => setMenuOpen(o => !o)}
                  onKeyDown={onTriggerKey}
                  title={MODEL_LABELS[model].note}
                  aria-haspopup="listbox"
                  aria-expanded={menuOpen}
                >
                  {MODEL_LABELS[model].name}
                  <span className="model-pick-caret" aria-hidden="true" />
                </button>
              </div>

              {/* Closing the tab used to be the way to stop a turn. It isn't any
                  more, which is the point — so there has to be a real button. It
                  sits beside send rather than replacing it, because the composer
                  stays usable while a turn runs. */}
              {busy && (
                <button
                  className="btn-icon btn-stop"
                  onClick={() => void stopTurn(key)}
                  title="Stop this turn"
                  aria-label="Stop this turn"
                >
                  <HugeiconsIcon icon={StopIcon} size={17} strokeWidth={2} />
                </button>
              )}
              <button
                className="btn-icon btn-send"
                onClick={() => submitDraft(key)}
                disabled={!draft.trim()}
                title={queueing ? 'Queue' : 'Send'}
                aria-label={queueing ? 'Queue this message' : 'Send'}
              >
                <HugeiconsIcon
                  icon={queueing ? Layers01Icon : Sent02Icon}
                  size={17}
                  strokeWidth={2}
                />
              </button>
            </div>
          </div>

          {/* Outside the field on purpose — see `.composer-anchor` above, which
              is the whole reason this is not a child of the control that opens
              it. It stays mounted in both states so it has something to
              transition *from*: a menu that only exists while open can be
              animated in and never out, because by the time it would play there
              is no element left. `visibility` in the closed state is what keeps
              it out of the tab order and off a screen reader while it is still
              in the document. */}
          <div
            className={`model-menu traced${menuOpen ? ' is-open' : ''}`}
            ref={modelMenu}
            onKeyDown={onMenuKey}
            role="listbox"
            aria-label="Model for this conversation"
          >
            {MODELS.map(m => (
              <button
                key={m}
                type="button"
                role="option"
                aria-selected={m === model}
                className={`model-menu-item${m === model ? ' is-selected' : ''}`}
                // Not reachable while closed — `visibility: hidden` takes it out
                // of the tab order — but a stale tabindex is cheaper to prevent
                // than to debug if that ever stops being true.
                tabIndex={menuOpen ? 0 : -1}
                onClick={() => {
                  setMenuOpen(false);
                  void chooseModel(key, m);
                }}
              >
                <span className="model-menu-name">{MODEL_LABELS[m].name}</span>
                <span className="model-menu-note">{MODEL_LABELS[m].note}</span>
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* The other way to put this conversation down, at the other end of it.
            The one in the head is where a window's controls belong and it is a
            long way from where the hand is: the pill you pressed to open this
            was on the rail, at the foot of the screen, so the cursor arrives at
            the bottom of the window and the fold button is at the top of it.
            Opening and closing in quick succession meant crossing the whole
            panel twice.

            Deliberately quiet, and shaped like what it does rather than
            labelled: a shallow bar under the field, no border, no glyph, the
            faintest fill that still reads as a surface. It is a second way to
            do something that is already reachable, so it has to be findable
            without ever being the thing you notice. */}
        <button
          className="chatwin-drop"
          onClick={() => lowerThread(key)}
          aria-label="Fold this conversation down"
          title="Fold down"
        />
        </div>
    </>
  );
}
